import React, { useEffect, useMemo, useRef, useState } from 'react';
import TvecLayout from '../../components/TvecLayout';
import TvecDownloadAll from '../../components/TvecDownloadAll';
import AuthService from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance';
import { ALL_COURSES_ID, COURSE_PROGRAMMES, useTvecCourse } from '../../utils/tvecCourse';
import { PROCESSES, TEMPLATE_ITEMS } from '../../utils/tvecTemplates';

// Chart colours (validated with the dataviz palette checker: light surface).
const C_PRIMARY = '#0284c7'; // sky-600  - attendance line / weekday
const C_SECOND = '#d97706';  // amber-600 - weekend

const toNumber = (v) => Number(String(v ?? '0').replace(/[^\d.-]/g, '')) || 0;
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const icons = {
  users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  active: 'M13 10V3L4 14h7v7l9-11h-7z',
  sun: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  check: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  shield: 'M12 3l8 3v5c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V6l8-3z M8.5 12l2.5 2.5 4.5-5',
  doc: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  chart: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  folder: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z',
};

function Icon({ name, className = 'h-5 w-5' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true">
      <path d={icons[name]} />
    </svg>
  );
}

function StatTile({ label, value, icon, tone, loading, hint }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          {loading ? (
            <div className="mt-2 h-8 w-20 animate-pulse rounded bg-slate-100" />
          ) : (
            <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900">{value}</p>
          )}
          {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
        </div>
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}>
          <Icon name={icon} />
        </span>
      </div>
    </div>
  );
}

function Panel({ title, subtitle, action, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

// 30-day attendance: area + 2px line, recessive grid, crosshair tooltip, table view.
function AttendanceChart({ data }) {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [asTable, setAsTable] = useState(false);

  const W = 720;
  const H = 260;
  const pad = { l: 40, r: 12, t: 12, b: 28 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const max = Math.max(1, ...data.map((d) => d.count));
  const niceMax = (() => {
    const step = 10 ** Math.floor(Math.log10(max));
    return Math.ceil(max / step) * step;
  })();
  const x = (i) => pad.l + (data.length <= 1 ? 0 : (i / (data.length - 1)) * innerW);
  const y = (v) => pad.t + innerH - (v / niceMax) * innerH;

  const line = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d.count).toFixed(1)}`).join(' ');
  const area = `${line} L${x(data.length - 1).toFixed(1)},${pad.t + innerH} L${x(0).toFixed(1)},${pad.t + innerH} Z`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(niceMax * f));

  const onMove = (event) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || !data.length) return;
    const px = ((event.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((px - pad.l) / innerW) * (data.length - 1));
    setHover(Math.max(0, Math.min(data.length - 1, idx)));
  };

  const active = hover != null ? data[hover] : null;
  const total = data.reduce((s, d) => s + d.count, 0);
  const avg = data.length ? Math.round(total / data.length) : 0;

  return (
    <Panel
      title="Daily attendance"
      subtitle={`Students marked present, last ${data.length} days · average ${fmt(avg)} per day`}
      action={(
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-xs font-semibold">
          {[['Chart', false], ['Table', true]].map(([label, value]) => (
            <button
              key={label}
              type="button"
              onClick={() => setAsTable(value)}
              className={`rounded-md px-3 py-1 transition ${asTable === value ? 'bg-sky-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    >
      {asTable ? (
        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2 text-right">Present</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[...data].reverse().map((d) => (
                <tr key={d.fullDate}>
                  <td className="px-3 py-1.5 text-slate-700">{d.fullDate}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-900">{fmt(d.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={wrapRef} className="relative" onMouseMove={onMove} onMouseLeave={() => setHover(null)} onTouchMove={(e) => onMove(e.touches[0])}>
          <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Daily attendance for the last 30 days">
            <defs>
              <linearGradient id="tvecArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C_PRIMARY} stopOpacity="0.22" />
                <stop offset="100%" stopColor={C_PRIMARY} stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth="1" />
                <text x={pad.l - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#64748b">{fmt(t)}</text>
              </g>
            ))}
            {data.map((d, i) => (i % 5 === 0 || i === data.length - 1 ? (
              <text key={d.fullDate} x={x(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="#64748b">{d.date}</text>
            ) : null))}
            <path d={area} fill="url(#tvecArea)" />
            <path d={line} fill="none" stroke={C_PRIMARY} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            {active ? (
              <g>
                <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={pad.t + innerH} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3 3" />
                <circle cx={x(hover)} cy={y(active.count)} r="5" fill={C_PRIMARY} stroke="#ffffff" strokeWidth="2" />
              </g>
            ) : null}
          </svg>
          {active ? (
            <div
              className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg"
              style={{ left: `${(x(hover) / W) * 100}%`, top: 0 }}
            >
              <p className="font-semibold text-slate-900">{active.fullDate}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-slate-600">
                <span className="h-2 w-2 rounded-full" style={{ background: C_PRIMARY }} />
                <span className="tabular-nums font-semibold text-slate-900">{fmt(active.count)}</span> present
              </p>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

// Weekday vs weekend: thin segmented bar with the figures printed beside it.
function ScheduleSplit({ weekday, weekend }) {
  const total = weekday + weekend;
  const wd = total ? (weekday / total) * 100 : 0;
  const rows = [
    { label: 'Weekday', value: weekday, color: C_PRIMARY, pct: wd },
    { label: 'Weekend', value: weekend, color: C_SECOND, pct: total ? 100 - wd : 0 },
  ];
  return (
    <Panel title="Active students by schedule" subtitle="Students in ongoing batches">
      <p className="text-4xl font-bold tabular-nums tracking-tight text-slate-900">{fmt(total)}</p>
      <div className="mt-3 flex h-2.5 w-full gap-0.5 overflow-hidden rounded-full bg-slate-100" role="img" aria-label={`Weekday ${fmt(weekday)}, weekend ${fmt(weekend)}`}>
        {rows.map((r) => (r.pct > 0 ? <div key={r.label} style={{ width: `${r.pct}%`, background: r.color }} className="first:rounded-l-full last:rounded-r-full" /> : null))}
      </div>
      <ul className="mt-4 space-y-2.5">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-slate-700">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.color }} />
              {r.label}
            </span>
            <span className="tabular-nums text-slate-900">
              <span className="font-semibold">{fmt(r.value)}</span>
              <span className="ml-2 text-xs text-slate-500">{Math.round(r.pct)}%</span>
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// The documents available for the course selected in the top bar, by process stage.
function CourseDocuments({ course }) {
  const isAll = course.id === ALL_COURSES_ID;
  const stages = useMemo(
    () => PROCESSES
      .map((proc) => ({
        proc,
        items: TEMPLATE_ITEMS.filter((it) => it.process === proc.id
          && (isAll ? COURSE_PROGRAMMES.some((c) => c.id === it.programme) : it.programme === course.id)),
      }))
      .filter((s) => s.items.length > 0),
    [course.id, isAll],
  );
  const total = stages.reduce((n, s) => n + s.items.length, 0);

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Documents · {course.label}</h2>
          <p className="text-xs text-slate-500">
            {total} documents across {stages.length} process stages{isAll ? ' (all courses)' : ''}. Change the course from the top bar.
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <a href="/tvec/template" className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-xs font-semibold text-sky-700 no-underline hover:bg-sky-100">
            Open templates
          </a>
          <TvecDownloadAll course={course} />
        </div>
      </div>
      {stages.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500">
          No documents have been added for {course.label} yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stages.map(({ proc, items }) => {
            const codes = Array.from(new Set(items.map((it) => it.code).filter(Boolean)));
            // "All courses": how many of this stage's documents belong to each course.
            const perCourse = isAll
              ? COURSE_PROGRAMMES
                .map((c) => ({ c, n: items.filter((it) => it.programme === c.id).length }))
                .filter((x) => x.n > 0)
              : [];
            return (
              <a
                key={proc.id}
                href="/tvec/template"
                className="group block rounded-2xl border border-slate-200 bg-white p-5 no-underline shadow-sm transition hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-sm font-semibold text-slate-900">{proc.label}</h3>
                  <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-sm font-bold tabular-nums text-sky-700">{items.length}</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{proc.hint}</p>
                {perCourse.length ? (
                  <p className="mt-3 flex flex-wrap gap-1.5">
                    {perCourse.map(({ c, n }) => (
                      <span key={c.id} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{c.short} {n}</span>
                    ))}
                  </p>
                ) : null}
                {codes.length ? (
                  <p className="mt-2 flex flex-wrap gap-1.5">
                    {codes.map((c) => (
                      <span key={c} className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">{c}</span>
                    ))}
                  </p>
                ) : null}
              </a>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function TvecDashboard() {
  const { course } = useTvecCourse();
  const [stats, setStats] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const staff = AuthService.getUser()?.superAdmin?.staff || {};
  const name = staff.name || AuthService.getUser()?.superAdmin?.name || '';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axiosInstance.get('/admin/dashboard/stats'),
      axiosInstance.get('/admin/dashboard/attendance'),
    ])
      .then(([s, a]) => {
        if (cancelled) return;
        setStats(s?.data?.stats ?? []);
        setAttendance(a?.data?.allCoursesAttendanceData ?? []);
      })
      .catch(() => { if (!cancelled) setError('Some figures could not be loaded. Please refresh to try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const byName = useMemo(() => Object.fromEntries(stats.map((s) => [s.name, toNumber(s.value)])), [stats]);

  const tiles = [
    { label: 'Total students', key: 'Total Students', icon: 'users', tone: 'bg-sky-50 text-sky-600' },
    { label: 'Active students', key: 'Active Students', icon: 'active', tone: 'bg-indigo-50 text-indigo-600' },
    { label: 'Weekday', key: 'Weekday Students', icon: 'sun', tone: 'bg-emerald-50 text-emerald-600' },
    { label: 'Weekend', key: 'Weekend Students', icon: 'calendar', tone: 'bg-amber-50 text-amber-600' },
    { label: 'Present today', key: 'Present Today', icon: 'check', tone: 'bg-violet-50 text-violet-600' },
  ];

  return (
    <TvecLayout title="TVEC Dashboard">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-700 via-sky-600 to-indigo-600 p-6 text-white shadow-lg sm:p-8">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -bottom-20 right-24 h-48 w-48 rounded-full bg-white/10" />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-sky-100">TVEC workspace</p>
              <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
                {greeting}{name ? `, ${name.split(' ')[0]}` : ''}
              </h1>
              <p className="mt-1 text-sm text-sky-100">{today}</p>
              <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold backdrop-blur">
                <span className="h-2 w-2 rounded-full bg-emerald-300" />
                Course: {course.label}
              </p>
            </div>
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
              <Icon name="shield" className="h-8 w-8" />
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
        ) : null}

        <p className="-mb-3 text-xs text-slate-500">Student and attendance figures below cover all courses in the institute.</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {tiles.map((t) => (
            <StatTile key={t.key} label={t.label} value={fmt(byName[t.key])} icon={t.icon} tone={t.tone} loading={loading} />
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {loading ? (
              <div className="h-80 animate-pulse rounded-2xl border border-slate-200 bg-white" />
            ) : (
              <AttendanceChart data={attendance} />
            )}
          </div>
          {loading ? (
            <div className="h-80 animate-pulse rounded-2xl border border-slate-200 bg-white" />
          ) : (
            <ScheduleSplit weekday={byName['Weekday Students'] || 0} weekend={byName['Weekend Students'] || 0} />
          )}
        </div>

        <CourseDocuments course={course} />
      </div>
    </TvecLayout>
  );
}
